import React, { useState, useEffect } from "react";
import {
  IndianRupee, ShoppingBag, TrendingUp, Star, Clock,
  CalendarDays, Fingerprint, Wallet, Package, GitMerge,
  Eye, EyeOff, X, ShieldCheck
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../utils/api";
import { globalAppCache } from "../utils/cache";
import { useAuth } from "./authentication/AuthContext";
import ClosingReportModal from "./ClosingReportModal";
import OnlineOrdersPanel from "./OnlineOrdersPanel";
import ReservationsPanel from "./ReservationsPanel";
import AttendancePanel from "./AttendancePanel";
import CashDrawerPanel from "./CashDrawerPanel";
import CombosPanel from "./CombosPanel";
import MergeOrdersModal from "./MergeOrdersModal";

/**
 * Dark quick-stats strip with today's KPIs + operational quick actions.
 * Supports:
 *  - Countertop Privacy Mode (1-click eye mask for customer confidentiality)
 *  - Dismiss/Hide with re-enable via Admin Dashboard
 *  - Role-based sales masking (Cashier vs Manager)
 *  - "Shortcuts Only" mode (shows action buttons without exposing revenue)
 */
export default function QuickStatsStrip({
  onAddCombo,
  hasActiveOrder = false,
  onOrderMerged,
  extraControls = null,
}) {
  const { isStaff, staffRole, restaurantId, user } = useAuth();
  const userId = restaurantId || user?.id || user?._id;
  const isManager = !isStaff || staffRole === "manager";
  const isCashierUp = !isStaff || ["manager", "cashier"].includes(staffRole);

  const [posSettings, setPosSettings] = useState(() => {
    const cached = userId ? globalAppCache.userCache[userId]?.posSettings : null;
    return (
      cached || {
        showQuickStatsStrip: true,
        statsVisibility: "full",
        maskSalesByDefault: false,
        restrictStatsToManager: false,
      }
    );
  });

  const [isPrivacyMasked, setIsPrivacyMasked] = useState(() => {
    const saved = localStorage.getItem("pos_privacy_mode");
    if (saved !== null) return saved === "true";
    return Boolean(posSettings?.maskSalesByDefault);
  });

  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [todayStats, setTodayStats] = useState({ revenue: 0, orders: 0, avgOrder: 0, topItem: null });
  const [activeHappyHour, setActiveHappyHour] = useState(null);
  const [activePanel, setActivePanel] = useState(null);

  // Sync settings from cache / user API & listen for admin updates
  useEffect(() => {
    if (!userId) return;

    if (globalAppCache.userCache[userId]?.posSettings) {
      setPosSettings(globalAppCache.userCache[userId].posSettings);
    } else {
      api.get(`user/${userId}`)
        .then((res) => {
          if (res.data) {
            globalAppCache.userCache[userId] = res.data;
            if (res.data.posSettings) {
              setPosSettings(res.data.posSettings);
            }
          }
        })
        .catch(() => {});
    }

    const onSettingsUpdate = (e) => {
      if (e.detail) {
        setPosSettings(e.detail);
      }
    };
    window.addEventListener("posSettingsUpdated", onSettingsUpdate);
    return () => window.removeEventListener("posSettingsUpdated", onSettingsUpdate);
  }, [userId]);

  // If default mask changes and no local preference exists
  useEffect(() => {
    const saved = localStorage.getItem("pos_privacy_mode");
    if (saved === null && posSettings?.maskSalesByDefault !== undefined) {
      setIsPrivacyMasked(Boolean(posSettings.maskSalesByDefault));
    }
  }, [posSettings?.maskSalesByDefault]);

  // Computed visibility states
  const isStripVisible = posSettings.showQuickStatsStrip && posSettings.statsVisibility !== "hidden";
  const hideSalesMetrics =
    posSettings.statsVisibility === "actions_only" ||
    (posSettings.restrictStatsToManager && !isManager) ||
    !isCashierUp;

  // Privacy mask toggle (persists to localStorage for the counter terminal)
  const togglePrivacyMask = () => {
    setIsPrivacyMasked((prev) => {
      const next = !prev;
      localStorage.setItem("pos_privacy_mode", String(next));
      return next;
    });
  };

  // ⌨️ Global Keyboard Shortcut: Alt + P anywhere on POS to toggle privacy
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.altKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        togglePrivacyMask();
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  // ⏱️ Auto-Privacy Timer: automatically re-mask sales after inactivity
  useEffect(() => {
    const timeoutSec = posSettings?.autoMaskTimeout ?? 45;
    if (isPrivacyMasked || timeoutSec <= 0 || !isStripVisible || hideSalesMetrics) {
      return;
    }

    let timer = null;
    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setIsPrivacyMasked(true);
        localStorage.setItem("pos_privacy_mode", "true");
        toast.info("Sales numbers auto-masked for countertop privacy (Press Alt+P to reveal)", {
          autoClose: 3500,
          toastId: "auto-mask-sales-toast",
        });
      }, timeoutSec * 1000);
    };

    resetTimer();

    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart"];
    const onActivity = () => resetTimer();
    activityEvents.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      if (timer) clearTimeout(timer);
      activityEvents.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, [isPrivacyMasked, posSettings?.autoMaskTimeout, isStripVisible, hideSalesMetrics]);

  // Hide strip confirmation
  const handleConfirmDismiss = async () => {
    setShowDismissConfirm(false);
    const updated = { ...posSettings, showQuickStatsStrip: false };
    setPosSettings(updated);

    try {
      if (userId) {
        await api.put(`user/${userId}`, { posSettings: updated });
        if (globalAppCache.userCache[userId]) {
          globalAppCache.userCache[userId].posSettings = updated;
        }
      }
      toast.info("Today's sales strip hidden. You can re-enable it anytime from Admin Dashboard > Profile & Settings.", {
        autoClose: 6000,
      });
    } catch (err) {
      console.error("Failed to dismiss strip:", err);
    }
  };

  // Fetch stats only if strip is visible & sales metrics aren't disabled
  useEffect(() => {
    if (!isStripVisible || hideSalesMetrics) return;

    api.get("/analytics/merchant-stats?filter=today")
      .then((res) => {
        const d = res.data.data;
        setTodayStats({
          revenue:  d.kpis?.revenue?.value      || 0,
          orders:   d.kpis?.billsCount?.value   || 0,
          avgOrder: d.kpis?.avgBillValue?.value || 0,
          topItem:  d.topItems?.[0]?.name       || null,
        });
      })
      .catch(() => {});
  }, [isStripVisible, hideSalesMetrics]);

  // Happy hours polling
  useEffect(() => {
    if (!isStripVisible) return;
    const check = () => {
      api.get("/happy-hours/active")
        .then((res) => setActiveHappyHour(res.data.data || null))
        .catch(() => {});
    };
    check();
    const timer = setInterval(check, 60000);
    return () => clearInterval(timer);
  }, [isStripVisible]);

  if (!isStripVisible) {
    return null;
  }

  const actionBtn = "w-8 h-8 rounded-xl hover:bg-white/10 text-slate-500 hover:text-slate-200 transition-colors flex items-center justify-center";

  return (
    <>
      <div className="print:hidden sticky top-14 z-30 shrink-0 bg-gray-900 px-4 py-2.5 sm:py-3 flex items-center gap-3 overflow-x-auto scrollbar-none border-b border-white/5 transition-all">

        {/* TODAY badge */}
        <span className="shrink-0 bg-teal-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg shadow-lg shadow-teal-500/20">
          TODAY
        </span>

        {/* Countertop Privacy Mode (Eye toggle) */}
        {!hideSalesMetrics && (
          <button
            onClick={togglePrivacyMask}
            title={
              isPrivacyMasked
                ? "Reveal sales figures (Alt+P) · Auto-masks after inactivity"
                : "Hide sales figures from customers (Alt+P)"
            }
            className={`shrink-0 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg transition-all ${
              isPrivacyMasked
                ? "bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 shadow-sm"
                : "bg-white/10 text-slate-300 border border-white/10 hover:bg-white/20 hover:text-white"
            }`}
          >
            {isPrivacyMasked ? (
              <>
                <EyeOff size={13} className="text-amber-400" />
                <span className="hidden sm:inline">Hidden</span>
              </>
            ) : (
              <>
                <Eye size={13} />
                <span className="hidden sm:inline">Privacy</span>
              </>
            )}
          </button>
        )}

        {/* Happy Hours badge — visible when active */}
        {activeHappyHour && (
          <span className="shrink-0 flex items-center gap-1.5 bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg shadow-lg shadow-amber-500/30 animate-pulse">
            <Clock size={11} />
            Happy Hours · -{activeHappyHour.discountPercentage}%
          </span>
        )}

        {/* When sales figures are hidden via policy (Shortcuts Only or Non-Manager), show subtle badge */}
        {hideSalesMetrics ? (
          <span className="shrink-0 flex items-center gap-1.5 bg-white/5 text-slate-400 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border border-white/5">
            <ShieldCheck size={13} className="text-teal-400" />
            Quick Actions
          </span>
        ) : (
          <>
            <div className="w-px h-7 bg-white/10 shrink-0"/>

            {/* Revenue */}
            <div className="flex items-center gap-2.5 shrink-0">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center shadow-inner">
                <IndianRupee size={15} className="text-emerald-400"/>
              </div>
              <div>
                <p className="text-[9px] text-slate-500 leading-none font-semibold uppercase tracking-widest">Revenue</p>
                <p className="text-base font-black text-white leading-tight mt-0.5">
                  {isPrivacyMasked ? (
                    <span className="font-mono tracking-widest text-slate-300 select-none">₹••••••</span>
                  ) : (
                    `₹${todayStats.revenue.toLocaleString("en-IN")}`
                  )}
                </p>
              </div>
            </div>

            <div className="w-px h-7 bg-white/10 shrink-0"/>

            {/* Orders */}
            <div className="flex items-center gap-2.5 shrink-0">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shadow-inner">
                <ShoppingBag size={15} className="text-blue-400"/>
              </div>
              <div>
                <p className="text-[9px] text-slate-500 leading-none font-semibold uppercase tracking-widest">Orders</p>
                <p className="text-base font-black text-white leading-tight mt-0.5">
                  {isPrivacyMasked ? (
                    <span className="font-mono tracking-widest text-slate-300 select-none">••</span>
                  ) : (
                    todayStats.orders
                  )}
                </p>
              </div>
            </div>

            <div className="w-px h-7 bg-white/10 shrink-0"/>

            {/* Avg Order */}
            <div className="flex items-center gap-2.5 shrink-0">
              <div className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/25 flex items-center justify-center shadow-inner">
                <TrendingUp size={15} className="text-violet-400"/>
              </div>
              <div>
                <p className="text-[9px] text-slate-500 leading-none font-semibold uppercase tracking-widest">Avg Order</p>
                <p className="text-base font-black text-white leading-tight mt-0.5">
                  {isPrivacyMasked ? (
                    <span className="font-mono tracking-widest text-slate-300 select-none">₹••••</span>
                  ) : (
                    `₹${Math.round(todayStats.avgOrder).toLocaleString("en-IN")}`
                  )}
                </p>
              </div>
            </div>

            {/* Top Item */}
            {todayStats.topItem && (
              <>
                <div className="w-px h-7 bg-white/10 shrink-0"/>
                <div className="flex items-center gap-2.5 shrink-0">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center shadow-inner">
                    <Star size={15} className="text-amber-400"/>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500 leading-none font-semibold uppercase tracking-widest">Top Item</p>
                    <p className="text-sm font-black text-white leading-tight mt-0.5 max-w-[120px] truncate">
                      {isPrivacyMasked ? (
                        <span className="font-mono tracking-widest text-slate-300 select-none">••••••</span>
                      ) : (
                        todayStats.topItem
                      )}
                    </p>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Quick actions — push to right */}
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          {isManager && (
            <>
              <button onClick={() => setActivePanel("closingReport")} title="Daily closing report" className={actionBtn}>
                <IndianRupee size={14}/>
              </button>
              <button onClick={() => setActivePanel("onlineOrders")} title="Online orders (Swiggy / Zomato)" className={actionBtn}>
                <ShoppingBag size={14}/>
              </button>
            </>
          )}
          <button onClick={() => setActivePanel("reservations")} title="Table reservations" className={actionBtn}>
            <CalendarDays size={14}/>
          </button>
          <button onClick={() => setActivePanel("attendance")} title="Attendance & shifts" className={actionBtn}>
            <Fingerprint size={14}/>
          </button>
          {isCashierUp && (
            <button onClick={() => setActivePanel("cashDrawer")} title="Cash drawer / petty cash" className={actionBtn}>
              <Wallet size={14}/>
            </button>
          )}
          <button onClick={() => setActivePanel("combos")} title="Combo deals" className={actionBtn}>
            <Package size={14}/>
          </button>
          {isCashierUp && (
            <button onClick={() => setActivePanel("mergeOrders")} title="Merge orders (join tables)" className={actionBtn}>
              <GitMerge size={14}/>
            </button>
          )}
          {extraControls}

          {/* Remove / Dismiss Strip (Manager only) */}
          {isManager && (
            <>
              <div className="w-px h-5 bg-white/10 shrink-0 mx-0.5"/>
              <button
                onClick={() => setShowDismissConfirm(true)}
                title="Hide Today's strip (Re-enable from Admin Dashboard > Profile & Settings)"
                className="w-8 h-8 rounded-xl hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors flex items-center justify-center shrink-0"
              >
                <X size={14}/>
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Dismiss Confirmation Modal ── */}
      {showDismissConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-200 text-slate-800 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <EyeOff size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Hide Sales Strip?</h3>
                <p className="text-xs text-slate-500">Remove from POS screen</p>
              </div>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              This will remove the black Today's Sales strip from your billing and table screens.
              <br /><br />
              You can easily re-enable it anytime from:
              <br />
              <strong className="text-slate-900 bg-slate-100 px-2 py-0.5 rounded mt-1 inline-block">
                Admin Dashboard &gt; Profile &amp; Settings
              </strong>
            </p>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                onClick={() => setShowDismissConfirm(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDismiss}
                className="px-4 py-2 text-xs font-bold bg-slate-900 hover:bg-black text-white rounded-xl transition shadow-sm"
              >
                Hide Strip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Panels ── */}
      {activePanel === "closingReport" && (
        <ClosingReportModal onClose={() => setActivePanel(null)} />
      )}
      {activePanel === "onlineOrders" && (
        <OnlineOrdersPanel
          onClose={() => setActivePanel(null)}
          onSelectOrder={(order) => {
            setActivePanel(null);
            toast.info(`Online order #${order.orderNumber} — open in billing panel`);
          }}
        />
      )}
      {activePanel === "reservations" && (
        <ReservationsPanel onClose={() => setActivePanel(null)} />
      )}
      {activePanel === "attendance" && (
        <AttendancePanel onClose={() => setActivePanel(null)} />
      )}
      {activePanel === "cashDrawer" && (
        <CashDrawerPanel onClose={() => setActivePanel(null)} />
      )}
      {activePanel === "combos" && (
        <CombosPanel
          onClose={() => setActivePanel(null)}
          onAddCombo={onAddCombo}
          hasActiveOrder={hasActiveOrder}
        />
      )}
      {activePanel === "mergeOrders" && (
        <MergeOrdersModal
          onClose={() => setActivePanel(null)}
          onMerged={(order) => onOrderMerged && onOrderMerged(order)}
        />
      )}
    </>
  );
}
